/** Formatting helpers shared across apps. */

import type { CSSProperties } from "react";

export interface CurrencyOpts {
  currencyCode?: string;
  precision?: number;
}

/** Formats an integer amount (in the store's smallest currency unit) using its currency code & display precision. Defaults to MMK/0. */
export function formatMoney(amount: number | null | undefined, opts?: CurrencyOpts): string {
  const precision = opts?.precision ?? 0;
  const currency = opts?.currencyCode ?? "MMK";
  const value = precision > 0 ? (Number(amount) || 0) / 10 ** precision : Math.round(Number(amount) || 0);
  return `${currency} ${value.toLocaleString("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })}`;
}

/** First letters of up to the first two words, e.g. "John Smith" -> "JS". */
export function initials(name: string | null | undefined): string {
  return (name ?? "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function uid(prefix = "id"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

const SWITCH_SIZES = {
  md: { width: 44, height: 26, knobSize: 20, radius: 13 },
  sm: { width: 40, height: 23, knobSize: 17, radius: 14 },
} as const;

/** Toggle-switch track/knob styles. */
export function switchStyle(
  on: boolean,
  size: keyof typeof SWITCH_SIZES = "md",
): { track: CSSProperties; knob: CSSProperties } {
  const { width, height, knobSize, radius } = SWITCH_SIZES[size];
  const pad = (height - knobSize) / 2;
  return {
    track: {
      width,
      height,
      borderRadius: radius,
      background: on ? "#DC2D49" : "#D5D7DD",
      position: "relative",
      cursor: "pointer",
      transition: "background .15s",
      flex: "0 0 auto",
    },
    knob: {
      position: "absolute",
      top: pad,
      left: on ? width - knobSize - pad : pad,
      width: knobSize,
      height: knobSize,
      borderRadius: "50%",
      background: "#F8F9FA",
      transition: "left .15s",
      boxShadow: "0 1px 3px rgba(0,0,0,.2)",
    },
  };
}
